<template>
    <v-container class="pa-2">
        <v-row dense>
            <v-col cols="12" :md="isMd ? 12 : 6" :offset-md="isMd ? 0 : 3" sm="8" offset-sm="2">

                <!-- Header sticky -->
                <v-card class="rounded-xl elevation-2 px-3 py-2 mb-3 sticky-header">
                    <div class="d-flex align-center">
                        <v-icon color="primary" class="mr-2">mdi-magnify</v-icon>
                        <div class="text-subtitle-2 font-weight-bold primary--text flex-grow-1">Ricerche</div>
                    </div>
                </v-card>

                <!-- Filtri -->
                <v-card class="rounded-xl elevation-2 mb-3">
                    <div class="section-header">
                        <v-icon x-small color="grey darken-1" class="mr-1">mdi-filter-outline</v-icon>
                        <span class="flex-grow-1">Filtri</span>
                        <template v-if="interno === '1'">
                            <v-btn v-if="!filePdf" x-small rounded outlined color="pink" :loading="loadingPdf" @click="programmaPdf" class="mr-1">
                                <v-icon x-small left>mdi-file-pdf-box</v-icon>PDF
                            </v-btn>
                            <v-btn v-else x-small rounded color="pink" dark @click="visualizzaPdf" class="mr-1">
                                <v-icon x-small left>mdi-magnify</v-icon>PDF
                            </v-btn>
                        </template>
                        <v-btn v-if="!fileExcel" x-small rounded outlined color="success" :loading="loadingExcel" @click="programmaExcel">
                            <v-icon x-small left>mdi-microsoft-excel</v-icon>Excel
                        </v-btn>
                        <v-btn v-else x-small rounded color="success" dark @click="visualizzaExcel">
                            <v-icon x-small left>mdi-magnify</v-icon>Excel
                        </v-btn>
                    </div>
                    <v-card-text class="pt-1 pb-3 px-3">

                        <div class="filtri-inner">

                            <!-- Tipo interni/esterni -->
                            <v-btn-toggle v-model="interno" mandatory dense rounded color="primary" class="mb-3 d-flex" @change="onChangeTipo">
                                <v-btn value="1" small class="flex-grow-1">
                                    <v-icon small left>mdi-home</v-icon>
                                    Interni
                                </v-btn>
                                <v-btn value="2" small class="flex-grow-1">
                                    <v-icon small left>mdi-account-arrow-right-outline</v-icon>
                                    Esterni{{ idOratoreFix ? ' (personali)' : '' }}
                                </v-btn>
                            </v-btn-toggle>

                            <!-- Oratore (solo se non fisso) -->
                            <v-autocomplete v-if="!idOratoreFix"
                                v-model="idOratore"
                                :items="oratori"
                                item-value="id"
                                item-text="nominativo"
                                :filter="filterOratori"
                                outlined rounded dense
                                label="Oratore (opzionale)"
                                prepend-inner-icon="mdi-account-outline"
                                clearable
                                no-data-text="Nessun oratore"
                                class="mb-2"
                                @change="changeOratore">
                                <template v-slot:item="{ item }">
                                    <v-list-item-content>
                                        <v-list-item-title>{{ item.nominativo }}</v-list-item-title>
                                        <v-list-item-subtitle v-if="item.congregazione">{{ item.congregazione.nome }}</v-list-item-subtitle>
                                    </v-list-item-content>
                                </template>
                            </v-autocomplete>

                            <!-- Schema (solo interni) -->
                            <v-autocomplete v-if="interno === '1'"
                                v-model="idDiscorso"
                                :items="schemiFlat"
                                item-value="id"
                                :filter="filterSchemi"
                                outlined rounded dense
                                label="Schema (opzionale)"
                                prepend-inner-icon="mdi-book-open-outline"
                                clearable
                                no-data-text="Nessuno schema"
                                class="mb-2"
                                @change="filtra">
                                <template v-slot:item="{ item }">
                                    <v-list-item-content>
                                        <v-list-item-title>
                                            <strong>({{ item.diId === 99999 ? '-' : item.diId }})</strong>
                                            {{ item.tema }}
                                        </v-list-item-title>
                                    </v-list-item-content>
                                </template>
                            </v-autocomplete>

                            <!-- Date -->
                            <div class="d-flex" style="gap:8px">
                                <v-text-field v-model="dataInizio"
                                    outlined rounded dense
                                    label="Da"
                                    type="date"
                                    @input="filtra" />
                                <v-text-field v-model="dataFine"
                                    outlined rounded dense
                                    label="A"
                                    type="date"
                                    @input="filtra" />
                            </div>

                        </div>

                    </v-card-text>
                </v-card>

                <!-- Risultati — tabella su md+, cards su mobile -->

                <!-- TABELLA (tablet/desktop) -->
                <v-card v-if="isMd" class="rounded-xl elevation-2 mb-3">
                    <v-data-table
                        :loading="loading"
                        :headers="headers"
                        :items="programmi"
                        :items-per-page="15"
                        :custom-sort="customSort"
                        @update:sort-by="v => sortField = v || 'data'"
                        @update:sort-desc="v => sortDesc = !!v"
                        class="striped-table"
                        loading-text="Caricamento..."
                        no-data-text="Nessun appuntamento trovato"
                        no-results-text="Nessun appuntamento corrispondente"
                        :footer-props="{
                            'items-per-page-text': 'Per pagina',
                            'items-per-page-all-text': 'Tutti',
                            'items-per-page-options': [10, 15, 25, 50, -1]
                        }">
                        <template v-slot:item.data="{ item }">
                            {{ localData(item.data) }}
                        </template>
                        <template v-slot:item.congregazioneEffettiva="{ item }">
                            <span v-if="item.isVisita"><strong>Visita del Sorvegliante</strong></span>
                            <span v-else-if="item.isSpeciale"><strong>Evento Speciale</strong></span>
                            <span v-else-if="item.isCongresso"><strong>Assemblea/Congresso</strong></span>
                            <span v-else-if="idOratore === '0' && interno === '1' && item.isStandard && item.congregazioneEsterna">{{ item.congregazioneEsterna.nome }}</span>
                            <span v-else-if="idOratore === '0' && interno === '1' && item.isExtra">{{ item.congregazioneExtra }}</span>
                            <span v-else-if="idOratore === '0' && interno === '1' && item.isStandard && item.congregazione">{{ item.congregazione.nome }}</span>
                            <span v-else-if="idOratore === '0' && interno === '2' && item.isStandard && item.congregazione">{{ item.congregazione.nome }}</span>
                            <span v-else-if="idOratore === '0' && interno === '2' && item.isExtra">{{ item.congregazioneExtra }}</span>
                            <span v-else-if="idOratore !== '0' && item.isStandard && item.congregazione">{{ item.congregazione.nome }}</span>
                            <span v-else-if="idOratore !== '0' && item.isExtra">{{ item.congregazioneExtra }}</span>
                        </template>
                        <template v-slot:item.oratoreEffettivo="{ item }">
                            <span v-if="item.oratore">{{ item.oratore.nominativo }}</span>
                            <span v-else-if="item.oratoreExtra">{{ item.oratoreExtra }}</span>
                            <span v-else-if="item.isVisita">{{ item.sorvegliante }}</span>
                        </template>
                        <template v-slot:item.temaEffettivo="{ item }">
                            <span v-if="item.discorso && item.discorso.diId == 99999">( - ) {{ item.discorso.tema }}</span>
                            <span v-else-if="item.discorso"><strong>({{ item.discorso.diId }})</strong> {{ item.discorso.tema }}</span>
                            <span v-else-if="item.isVisita">{{ item.teamVisita }}</span>
                        </template>
                    </v-data-table>
                </v-card>

                <!-- CARDS (mobile) -->
                <template v-else>
                    <!-- Barra ordinamento -->
                    <div v-if="!loading && programmi.length > 0"
                        class="d-flex align-center mb-2 sort-bar">
                        <v-icon x-small class="mr-1 grey--text">mdi-sort</v-icon>
                        <v-chip v-for="opt in sortOpzioni" :key="opt.value"
                            x-small class="mr-1"
                            :color="sortField === opt.value ? 'primary' : ''"
                            :dark="sortField === opt.value"
                            @click="onSortChip(opt.value)">
                            {{ opt.text }}
                            <v-icon v-if="sortField === opt.value" x-small right>
                                {{ sortDesc ? 'mdi-arrow-down' : 'mdi-arrow-up' }}
                            </v-icon>
                        </v-chip>
                    </div>

                    <div v-if="loading" class="text-center py-6">
                        <v-progress-circular indeterminate color="primary" />
                    </div>
                    <template v-else>
                        <div v-if="programmi.length === 0" class="text-center py-6 grey--text text-body-2">
                            Nessun appuntamento trovato
                        </div>
                        <v-card v-for="p in programmiOrdinati" :key="p.id"
                            class="rounded-xl elevation-1 mb-2 px-3 py-2">
                            <div class="d-flex align-start">
                                <div class="flex-grow-1">
                                    <div class="text-caption grey--text mb-1">{{ localData(p.data) }}</div>
                                    <div class="text-body-2 font-weight-medium">
                                        <span v-if="p.discorso && p.discorso.diId == 99999">( - ) {{ p.discorso.tema }}</span>
                                        <span v-else-if="p.discorso">({{ p.discorso.diId }}) {{ p.discorso.tema }}</span>
                                        <span v-else-if="p.isVisita" class="primary--text font-weight-bold">Visita del Sorvegliante</span>
                                        <span v-else-if="p.isSpeciale" class="primary--text font-weight-bold">Evento Speciale</span>
                                        <span v-else-if="p.isCongresso" class="primary--text font-weight-bold">Assemblea/Congresso</span>
                                        <span v-if="p.isVisita && p.teamVisita" class="grey--text"> — {{ p.teamVisita }}</span>
                                    </div>
                                    <div class="text-caption mt-1">
                                        <v-icon x-small class="mr-1 grey--text">mdi-account-outline</v-icon>
                                        <span v-if="p.oratore">{{ p.oratore.nominativo }}</span>
                                        <span v-else-if="p.oratoreExtra">{{ p.oratoreExtra }}</span>
                                        <span v-else-if="p.isVisita">{{ p.sorvegliante }}</span>
                                        <span v-else class="grey--text">—</span>
                                    </div>
                                    <div class="text-caption mt-1">
                                        <v-icon x-small class="mr-1 grey--text">mdi-home-outline</v-icon>
                                        <span v-if="idOratore === '0' && interno === '1' && p.isStandard && p.congregazioneEsterna">{{ p.congregazioneEsterna.nome }}</span>
                                        <span v-else-if="idOratore === '0' && interno === '1' && p.isExtra && p.congregazioneExtra">{{ p.congregazioneExtra }}</span>
                                        <span v-else-if="idOratore === '0' && interno === '1' && p.isStandard && !p.congregazioneEsterna && p.congregazione">{{ p.congregazione.nome }}</span>
                                        <span v-else-if="idOratore === '0' && interno === '2' && p.isStandard && p.congregazione">{{ p.congregazione.nome }}</span>
                                        <span v-else-if="idOratore === '0' && interno === '2' && p.isExtra && p.congregazioneExtra">{{ p.congregazioneExtra }}</span>
                                        <span v-else-if="idOratore !== '0' && p.isStandard && p.congregazione">{{ p.congregazione.nome }}</span>
                                        <span v-else-if="idOratore !== '0' && p.isExtra">{{ p.congregazioneExtra }}</span>
                                        <span v-else-if="p.isVisita || p.isSpeciale || p.isCongresso" class="grey--text">—</span>
                                    </div>
                                </div>
                                <v-chip x-small
                                    :color="p.isProvvisorio ? 'orange' : p.isVisita ? 'deep-purple' : p.isSpeciale || p.isCongresso ? 'teal' : 'primary'"
                                    dark class="mt-1 ml-2">
                                    {{ p.isProvvisorio ? 'prov.' : p.isVisita ? 'visita' : p.isSpeciale ? 'spec.' : p.isCongresso ? 'congr.' : interno === '1' ? 'int.' : 'est.' }}
                                </v-chip>
                            </div>
                        </v-card>
                    </template>
                </template>

            </v-col>
        </v-row>
    </v-container>
</template>

<script>
import { callService, callPost, gLocalData, gDataCalendar, SUPERADMIN_CODE } from '@/modules/utilities.js'

export default {
    name: 'Ricerche',
    data: () => ({
        programmi: [],
        oratoriNostra: [],
        oratoriCircoscrizione: [],
        idOratore: '0',
        idOratoreFix: '',
        loading: false,
        interno: '1',
        dataInizio: '',
        dataFine: '',
        loadingPdf: false,
        filePdf: '',
        loadingExcel: false,
        fileExcel: '',
        schemi: [],
        idDiscorso: null,
        sortField: 'data',
        sortDesc: false,
    }),
    computed: {
        isMd() { return this.$vuetify.breakpoint.mdAndUp; },
        headers() {
            return [
                { text: 'Data',                                                                   value: 'data',                  width: '110px' },
                { text: this.interno === '1' ? 'Congregazione di Provenienza' : 'Congregazione', value: 'congregazioneEffettiva' },
                { text: 'Oratore',                                                                value: 'oratoreEffettivo' },
                { text: 'Schema',                                                                  value: 'temaEffettivo' },
            ];
        },
        schemiFlat() {
            return (this.schemi || []).map(s => ({
                id: s.discorso.id,
                diId: s.discorso.diId,
                numero: s.discorso.numero,
                tema: s.discorso.tema,
                text: `(${s.discorso.diId === 99999 ? '-' : s.discorso.diId}) ${s.discorso.tema}`,
            }));
        },
        oratori() {
            return this.interno === '1' ? this.oratoriCircoscrizione : this.oratoriNostra;
        },
        sortOpzioni() {
            return [
                { text: 'Data',          value: 'data' },
                { text: 'Congregazione', value: 'congregazioneEffettiva' },
                { text: 'Oratore',       value: 'oratoreEffettivo' },
                { text: 'Schema',        value: 'temaEffettivo' },
            ];
        },
        programmiOrdinati() {
            return this.customSort([...this.programmi], [this.sortField], [this.sortDesc]);
        },
    },
    methods: {
        localData(v) { return gLocalData(v); },

        filterOratori(item, queryText) {
            const q = queryText.toLowerCase();
            return (item.nominativo || '').toLowerCase().includes(q)
                || (item.congregazione?.nome || '').toLowerCase().includes(q);
        },

        filterSchemi(item, queryText) {
            const q = queryText.toLowerCase();
            return String(item.diId || '').includes(q)
                || (item.numero || '').toLowerCase().includes(q)
                || (item.tema || '').toLowerCase().includes(q);
        },

        buildParam(extra = {}) {
            return {
                partition: this.$store.state.partition,
                username: this.$store.state.oratore?.codice,
                token: this.$store.state.oratore?.token,
                idCircoscrizione: this.$store.state.idCircoscrizione,
                idCongregazione: this.$store.state.idCongregazione,
                interno: this.interno === '1',
                idOratore: this.idOratoreFix && this.interno === '2' ? this.idOratoreFix : (this.idOratore || '0'),
                idDiscorso: this.interno === '1' ? (this.idDiscorso || '') : '',
                dataInizio: this.dataInizio,
                dataFine: this.dataFine,
                sortBy: this.sortField,
                sortDesc: this.sortDesc,
                ...extra,
            };
        },

        async filtra() {
            this.loading = true;
            this.filePdf = '';
            this.fileExcel = '';
            const result = await callPost('/api/programma/ricerca', this.buildParam());
            if (result) {
                const sorv = this.$store.state.circoscrizione?.sorvegliante || '';
                this.programmi = result.map(p => ({ ...p, sorvegliante: p.isVisita ? sorv : '' }));
            } else {
                this.programmi = [];
            }
            this.loading = false;
        },

        async programmaPdf() {
            this.loadingPdf = true;
            this.filePdf = '';
            const out = await callPost('/api/programma/ricerca', this.buildParam({ stampa: true, pdf: true }));
            if (out) this.filePdf = out.fileOutput;
            this.loadingPdf = false;
        },
        visualizzaPdf() {
            const url = process.env.VUE_APP_DOCUMENT_SERVER + '/' + this.filePdf;
            const filename = this.filePdf.split('/').pop().replace(/_[0-9a-f]{4}\./, '.');
            if (window.HybridWebView) {
                this.$callMaui('DownloadAndOpenFile', `${url}|${filename}|nocache`);
            } else {
                const a = document.createElement('a');
                a.href = url; a.download = filename; a.target = '_blank'; a.click();
            }
            this.filePdf = '';
        },

        async programmaExcel() {
            this.loadingExcel = true;
            this.fileExcel = '';
            const out = await callPost('/api/programma/ricerca', this.buildParam({ stampa: true, pdf: false }));
            if (out) this.fileExcel = out.fileOutput;
            this.loadingExcel = false;
        },
        visualizzaExcel() {
            const url = process.env.VUE_APP_DOCUMENT_SERVER + '/' + this.fileExcel;
            const filename = this.fileExcel.split('/').pop().replace(/_[0-9a-f]{4}\./, '.');
            if (window.HybridWebView) {
                this.$callMaui('DownloadAndOpenFile', `${url}|${filename}|nocache`);
            } else {
                const a = document.createElement('a');
                a.href = url; a.download = filename; a.target = '_blank'; a.click();
            }
            this.fileExcel = '';
        },

        async onChangeTipo() {
            this.idOratore = '0';
            this.idDiscorso = null;
            await this.filtra();
        },

        async changeOratore() {
            if (!this.idOratore) this.idOratore = '0';
            await this.filtra();
        },

        onSortChip(field) {
            if (this.sortField === field) {
                this.sortDesc = !this.sortDesc;
            } else {
                this.sortField = field;
                this.sortDesc = false;
            }
        },

        customSort(items, sortBy, sortDesc) {
            if (!sortBy.length) return items;
            const field = sortBy[0];
            const desc = sortDesc[0];
            const val = (p) => {
                if (field === 'congregazioneEffettiva') {
                    if (p.isVisita || p.isSpeciale || p.isCongresso) return '';
                    return p.congregazioneEsterna?.nome || p.congregazioneExtra || p.congregazione?.nome || '';
                }
                if (field === 'oratoreEffettivo') {
                    return p.oratore?.nominativoOrder || p.oratore?.nominativo || p.oratoreExtra || p.sorvegliante || '';
                }
                if (field === 'temaEffettivo') {
                    if (!p.discorso) return p.isVisita ? 'zzz_visita' : p.isSpeciale ? 'zzz_speciale' : p.isCongresso ? 'zzz_congresso' : '';
                    const n = p.discorso.diId === 99999 ? 99999 : p.discorso.diId;
                    return String(n).padStart(6, '0') + ' ' + (p.discorso.tema || '');
                }
                return p[field] ?? '';
            };
            return [...items].sort((a, b) => {
                const cmp = String(val(a)).localeCompare(String(val(b)), 'it', { numeric: true });
                return desc ? -cmp : cmp;
            });
        },
    },
    async created() {
        const oggi = new Date();
        // Fine mese prossimo: giorno 0 del mese+2 = ultimo giorno del mese+1
        const fineMesseProssimo = new Date(oggi.getFullYear(), oggi.getMonth() + 2, 0);

        this.dataInizio = gDataCalendar(oggi.toISOString());
        this.dataFine   = gDataCalendar(fineMesseProssimo.toISOString());

        // Calcola subito idOratoreFix (prima di qualsiasi chiamata API)
        // così la combo oratori non compare mai e si evitano chiamate non autorizzate
        const or = this.$store.state.oratore;
        const isPrivilegiato = or?.codice == SUPERADMIN_CODE || or?.isAdmin || or?.isCoordinatore || or?.isAssistente;
        if (!isPrivilegiato) {
            this.idOratoreFix = or?.id || '';
        }

        // Carica lista schemi
        const listaSchemi = await callService('/api/discorso/schemiEstesi', [this.$store.state.idCircoscrizione]);
        this.schemi = listaSchemi || [];

        // Le liste oratori servono solo per la combo (non necessaria per oratori senza privilegi)
        if (isPrivilegiato) {
            // Oratori nostra congregazione → tab Esterni
            const listaNostra = await callService('/api/oratore/oratoriCongregazione', [this.$store.state.idCircoscrizione, this.$store.state.idCongregazione]);
            this.oratoriNostra = listaNostra || [];

            // Tutti gli oratori della circoscrizione → tab Interni
            const listaCirc = await callService('/api/oratore', [this.$store.state.idCircoscrizione]);
            this.oratoriCircoscrizione = listaCirc || [];
        }

        await this.filtra();
    },
}
</script>

<style scoped>
.sticky-header {
    position: sticky;
    top: 0;
    z-index: 4;
}
.section-header {
    display: flex;
    align-items: center;
    padding: 10px 14px 6px;
    font-size: 0.72rem;
    font-weight: 700;
    color: rgba(0,0,0,0.5);
    letter-spacing: 0.03em;
    text-transform: uppercase;
}
.theme--dark .section-header {
    color: rgba(255,255,255,0.45);
}

.filtri-inner {
    width: 100%;
    margin: 0 auto;
}

@media (min-width: 600px) {
    .filtri-inner {
        max-width: 420px;
    }
}

.sort-bar {
    overflow-x: auto;
    white-space: nowrap;
    padding-bottom: 2px;
}

/* alternanza righe */
.striped-table :deep(tbody tr:nth-child(odd) td) {
    background: rgba(0, 0, 0, 0.03);
}
.theme--dark .striped-table :deep(tbody tr:nth-child(odd) td) {
    background: rgba(255, 255, 255, 0.05);
}
</style>
